import { render } from './dom.js';
import { styleUebernehmen } from './styles.js';

/**
 * Basis für Container, die ihren Inhalt ohne vollständigen Seitenreload tauschen.
 *
 * Die Klasse ist bewusst frei von Wissen über konkrete Komponenten: Auslöser erkennt sie an Attributen am
 * Element, nicht an einer umgebenden Komponente, und alle Ziele — Inhaltscontainer, Scroll-Anker, Fokus,
 * Scroll-Versatz — kommen als Selektor aus dem Markup.
 *
 * Ableitende Elemente registrieren sich selbst und setzen ihren `__webpack_public_path__`, damit die
 * dynamischen Importe unten aus dem richtigen Verzeichnis geladen werden.
 */
export class FrameElement extends HTMLElement {
    #handler = null;

    async connectedCallback() {
        styleUebernehmen(this.localName);
        const { context } = this.dataset;
        switch (context) {
            case "fragment":
                await this.fragmentHandler();
                break;
            default:
                if ('navigation' in window) {
                    await this.navigationApiHandler();
                }
                break;
        }
    }

    async fragmentHandler() {
        try {
            const { FragmentHandler } = await import('./fragment.js');
            this.#handler = new FragmentHandler(this);
            this.#handler.connect();
        } catch (error) {
            console.error(error);
        }
    }

    async navigationApiHandler() {
        try {
            const { NavigationHandler } = await import('./navigation-api.js');
            this.#handler = new NavigationHandler(this);
            this.#handler.connect();
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Element, an das nach dem Inhaltswechsel gescrollt wird. Ohne `data-scroll-target` am Auslöser ist das
     * das Element selbst; mit Selektor der passende Nachfahre. Passt der Selektor auf kein Element, gilt
     * wieder das Element selbst — anders als bei `data-target` geht dabei kein Inhalt verloren.
     */
    #scrollAnchor(scrollTarget) {
        if (!scrollTarget) {
            return this;
        }
        return this.querySelector(scrollTarget) ?? this;
    }

    /**
     * Höhe eines überlagernden Elements, um die der Scroll nach oben korrigiert wird — typischerweise ein
     * klebender Seitenkopf. Welches Element das ist, steht per `data-scroll-offset-from` im Markup.
     */
    #versatzOben() {
        const selektor = this.dataset.scrollOffsetFrom;
        if (!selektor) {
            return 0;
        }
        const element = document.querySelector(selektor);
        return element?.getBoundingClientRect().height ?? 0;
    }

    #scrollToAnchor(scrollTarget) {
        const anchor = this.#scrollAnchor(scrollTarget);
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = prefersReducedMotion ? 'instant' : 'smooth';
        const top = anchor.getBoundingClientRect().top + window.scrollY - this.#versatzOben();
        window.scrollTo({ top, behavior });
    }

    /**
     * Zielcontainer für den Inhalt. Ohne `data-target` ist das das Element selbst; mit Selektor wird ein
     * Nachfahre gefüllt, sodass daneben liegender Inhalt (z. B. ein Slider) erhalten bleibt.
     *
     * Passt der Selektor auf kein Element, wird geworfen statt auf das Element selbst zurückzufallen: der
     * Fallback würde genau den Inhalt löschen, den `data-target` bewahren soll.
     */
    #target() {
        const { target } = this.dataset;
        if (!target) {
            return this;
        }
        const container = this.querySelector(target);
        if (!container) {
            throw new Error(`data-target passt auf kein Element: ${target}`);
        }
        return container;
    }

    #update(target, html, { scrollToContainer = true, scrollTarget = null, focusTarget = null } = {}) {
        // Prevents scroll container from clamping scrollTop to 0 while content is empty
        this.style.minHeight = `${this.offsetHeight}px`;
        target.replaceChildren();
        render(target, html);
        this.style.minHeight = '';
        this.#fokussiereNachTausch(target, focusTarget, scrollTarget);
        if (scrollToContainer) {
            this.#scrollToAnchor(scrollTarget);
        }
    }

    /**
     * Legt den Fokus dorthin zurück, wo der Wechsel ausgelöst wurde. Der Auslöser selbst wird beim Tausch
     * ersetzt, deshalb benennt `data-focus-target` den Bereich, in dem er liegt, und nicht ihn selbst — ein
     * einzelner Schalter kann im neuen Inhalt auch mal `disabled` sein und nähme den Fokus dann nicht an.
     *
     * Ohne Angabe bleibt es beim Standardverhalten der Navigation API (Fokus auf `<body>`).
     */
    #fokussiereNachTausch(target, focusTarget, scrollTarget) {
        if (!focusTarget) {
            return;
        }
        const ziel = target.querySelector(focusTarget);
        if (ziel && fokussiere(ziel)) {
            return;
        }
        fokussiere(this.#scrollAnchor(scrollTarget));
    }

    async updateContent(url, { scrollToContainer = true, scrollTarget = null, focusTarget = null, viewTransition = true, signal = null } = {}) {
        // Vor dem Fetch auflösen: ein Fehler im View-Transition-Callback landet in der Transition-Promise
        // und erreicht die Aufrufer nicht mehr, die sonst auf die native Navigation zurückfallen.
        const target = this.#target();
        const headlessUrl = new URL(url, window.location.href);
        headlessUrl.searchParams.set('headless', 'true');
        this.setAttribute('aria-busy', 'true');
        try {
            const html = await this.#lade(headlessUrl.toString(), signal);
            // Ein leeres Fragment ist ein Backend-Fallback (z. B. Timeout beim Artikelservice). Es darf den
            // bestehenden Inhalt nicht ersetzen, sonst bleibt der Bereich leer zurück.
            if (html.trim() === '') {
                throw new Error('Leeres Fragment');
            }
            const optionen = { scrollToContainer, scrollTarget, focusTarget };

            if (viewTransition && 'startViewTransition' in document) {
                document.startViewTransition(() => this.#update(target, html, optionen));
            } else {
                this.#update(target, html, optionen);
            }
        } finally {
            this.removeAttribute('aria-busy');
        }
    }

    /**
     * `signal` kommt aus dem `NavigateEvent` und bricht ab, sobald eine neue Navigation die laufende ablöst.
     * Ohne das gewönne bei schnell aufeinanderfolgenden Klicks die Antwort, die zuerst eintrifft — nicht die
     * zuletzt angeforderte.
     */
    async #lade(url, signal) {
        const response = await fetch(url, { signal });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
    }

    disconnectedCallback() {
        this.#handler?.cleanup();
        this.#handler = null;
    }
}

/**
 * Setzt den Fokus, auch auf ein Element, das von sich aus keinen annimmt — ein Container etwa. Ein
 * `disabled`-Element bleibt trotzdem außen vor, deshalb meldet die Funktion zurück, ob es geklappt hat.
 */
function fokussiere(element) {
    if (!element.hasAttribute('tabindex') && element.tabIndex < 0) {
        element.setAttribute('tabindex', '-1');
    }
    element.focus({ preventScroll: true });
    return document.activeElement === element;
}
